import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { EmploymentDetails } from '@kanda-libs/ks-frontend-services';

import { ApplyFormFieldValues } from 'pages/Apply/context';

export interface PrimaryEmploymentHook {
  employmentStatus: EmploymentDetails['employment_status'] | undefined;
  onGrossBlur: () => void;
}

export default function usePrimaryEmployment(): PrimaryEmploymentHook {
  const { unregister, trigger } = useFormContext();
  const employmentStatus = useWatch<
    ApplyFormFieldValues,
    'credit.employment_details.employment_status'
  >({
    name: 'credit.employment_details.employment_status',
  });

  const onGrossBlur = useCallback(
    () => trigger('credit.employment_details.gross_annual_income'),
    [trigger],
  );

  const employmentStatusRef = useRef<
    EmploymentDetails['employment_status'] | undefined
  >();

  const getEmploymentStatusFieldName = useCallback(
    (status: EmploymentDetails['employment_status']): string => {
      if (status === 'employed')
        return 'credit.employment_details.employed_details';
      if (status === 'self_employed')
        return 'credit.employment_details.self_employed_details';
      if (status === 'not_employed')
        return 'credit.employment_details.not_employed_details';
      return 'credit.employment_details.retired_details';
    },
    [],
  );

  useEffect(() => {
    if (!employmentStatus) return;
    const refStatus = employmentStatusRef.current;
    if (refStatus === undefined) {
      employmentStatusRef.current = employmentStatus;
      return;
    }
    if (employmentStatus === employmentStatusRef.current) return;
    const fieldName = getEmploymentStatusFieldName(refStatus);
    unregister(fieldName);
    employmentStatusRef.current = employmentStatus;
  }, [getEmploymentStatusFieldName, employmentStatus, unregister]);

  return {
    employmentStatus,
    onGrossBlur,
  };
}
